/**
 * lifecycle-diagnostics.ts — the « Cycle de vie » block of `ss doctor`.
 *
 * Inside a SmartStack project, says where the project stands in its lifecycle.
 * WARNINGS ONLY — never an error: `ss doctor` keeps its exit code (an outdated
 * socle is a decision, not a broken machine).
 *
 * - socle vs latest     — the project's SmartStack against the latest stable on NuGet;
 * - NuGet vs npm        — the compat matrix (socle-compat.ts): major.minor must match;
 * - skills vs CLI       — the deployed skills manifest against the running CLI
 *                         (`ss doctor --fix` runs the update);
 * - generator vs CLI    — the CLI that last generated/upgraded the project;
 * - unfinished upgrade  — `lastUpgrade.status` in-progress/partial, or a config socle
 *                         version that disagrees with the csproj files;
 * - backups             — how many `ss upgrade` backups the project keeps.
 *
 * Pure inputs (versions are passed in), so it is testable offline.
 */
import { compareVersions, parseVersion, readProjectSmartStackVersion } from './smartstack-version.js';
import { checkSocleCompat, normalizeNpmVersion } from './socle-compat.js';
import { getGenerator, getLastUpgrade, readProjectConfig } from './project-versions.js';
import { BackupSet } from './upgrade-backup.js';
import fs from 'fs-extra';
import { join } from 'path';

export interface LifecycleCheck {
  name: string;
  status: 'ok' | 'warning' | 'info';
  message: string;
  details?: string;
  fix?: string;
  /** `ss doctor --fix` may run `ss update` for this one. */
  runsSkillsUpdate?: boolean;
}

export interface LifecycleInputs {
  cliVersion: string;
  /** Deployed skills manifest version (null = not installed). */
  skillsVersion: string | null;
  /** Latest stable SmartStack on NuGet (null = registry unreachable / not checked). */
  latestSocle: string | null;
}

async function readFrontendNpmVersion(projectDir: string): Promise<string | null> {
  const webDir = join(projectDir, 'web');
  if (!(await fs.pathExists(webDir))) return null;
  for (const entry of await fs.readdir(webDir, { withFileTypes: true })) {
    if (!entry.isDirectory()) continue;
    try {
      const pkg = JSON.parse(await fs.readFile(join(webDir, entry.name, 'package.json'), 'utf-8')) as { dependencies?: Record<string, string> };
      const spec = pkg.dependencies?.['@atlashub/smartstack'];
      if (spec) return normalizeNpmVersion(spec) ?? spec;
    } catch {
      /* not a web app */
    }
  }
  return null;
}

/** Null when `projectDir` is not a SmartStack project (no `.smartstack/config.json`). */
export async function diagnoseLifecycle(projectDir: string, inputs: LifecycleInputs): Promise<LifecycleCheck[] | null> {
  const configRead = await readProjectConfig(projectDir);
  if (!configRead) return null;
  const checks: LifecycleCheck[] = [];
  if (!configRead.ok) {
    checks.push({ name: 'Project config', status: 'warning', message: configRead.message, fix: 'Fix the JSON syntax of .smartstack/config.json' });
    return checks;
  }
  const config = configRead.value;
  const socle = await readProjectSmartStackVersion(projectDir);
  const npm = await readFrontendNpmVersion(projectDir);

  // Socle vs latest
  if (!socle) {
    checks.push({ name: 'Socle version', status: 'info', message: 'No SmartStack PackageReference found' });
  } else if (!inputs.latestSocle) {
    checks.push({ name: 'Socle version', status: 'info', message: `SmartStack ${socle} — NuGet unreachable, latest not checked` });
  } else if (parseVersion(socle) && compareVersions(socle, inputs.latestSocle) < 0) {
    checks.push({ name: 'Socle version', status: 'warning', message: `SmartStack ${socle} → ${inputs.latestSocle} available`, fix: 'ss upgrade' });
  } else {
    checks.push({ name: 'Socle version', status: 'ok', message: `SmartStack ${socle} (latest)` });
  }

  // NuGet vs npm
  if (npm) {
    const compat = checkSocleCompat(socle, npm);
    if (compat.level === 'ok') checks.push({ name: 'NuGet / npm', status: 'ok', message: compat.message });
    else if (compat.level !== 'unknown') {
      checks.push({ name: 'NuGet / npm', status: 'warning', message: compat.message, fix: 'ss upgrade' });
    }
  }

  // Skills vs CLI
  if (inputs.skillsVersion === inputs.cliVersion) {
    checks.push({ name: 'Skills vs CLI', status: 'ok', message: `Skills at ${inputs.cliVersion}` });
  } else {
    checks.push({
      name: 'Skills vs CLI',
      status: 'warning',
      message: inputs.skillsVersion
        ? `Skills ${inputs.skillsVersion} ≠ CLI ${inputs.cliVersion}`
        : `No skills deployed (CLI ${inputs.cliVersion})`,
      fix: 'ss update (or ss doctor --fix)',
      runsSkillsUpdate: true,
    });
  }

  // Generator vs CLI
  const generator = getGenerator(config);
  if (!generator) {
    checks.push({ name: 'Generator version', status: 'info', message: 'Unknown (project predates generator tracking) — recorded by the next ss upgrade' });
  } else if (compareVersions(generator.cliVersion, inputs.cliVersion) < 0) {
    checks.push({
      name: 'Generator version',
      status: 'warning',
      message: `Project generated/upgraded by CLI ${generator.cliVersion}, this CLI is ${inputs.cliVersion}`,
      fix: 'ss upgrade',
    });
  } else {
    checks.push({ name: 'Generator version', status: 'ok', message: `CLI ${generator.cliVersion}` });
  }

  // Unfinished upgrade
  const last = getLastUpgrade(config);
  const configSocle = typeof config.smartStackVersion === 'string' ? config.smartStackVersion : null;
  if (last && (last.status === 'in-progress' || last.status === 'partial')) {
    checks.push({
      name: 'Last upgrade',
      status: 'warning',
      message: last.status === 'in-progress'
        ? `Upgrade of ${last.at} never finished (interrupted)`
        : `Upgrade of ${last.at} is PARTIAL (build/tests red after it)`,
      details: last.backupId ? `Backup: .smartstack/upgrade-backups/${last.backupId}/ — see .smartstack/upgrade-report.json` : undefined,
      fix: 'Fix and re-run ss upgrade, or undo: ss upgrade --restore latest',
    });
  } else if (socle && configSocle && parseVersion(configSocle) && configSocle !== socle) {
    checks.push({
      name: 'Last upgrade',
      status: 'warning',
      message: `.smartstack/config.json says SmartStack ${configSocle}, the csproj files reference ${socle}`,
      fix: 'ss upgrade (records the version once the restore succeeds)',
    });
  } else if (last) {
    checks.push({ name: 'Last upgrade', status: 'ok', message: `${last.status} on ${last.at.split('T')[0]} (CLI ${last.cliVersion})` });
  }

  // Backups
  const backups = await BackupSet.list(projectDir);
  if (backups.length > 0) {
    checks.push({
      name: 'Upgrade backups',
      status: backups.length > 10 ? 'warning' : 'info',
      message: `${backups.length} backup(s) in .smartstack/upgrade-backups/ (latest ${backups[0].id})`,
      fix: backups.length > 10 ? 'Delete the old folders under .smartstack/upgrade-backups/ you no longer need' : undefined,
    });
  }

  return checks;
}
